import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import type { KeyboardEvent, UIEvent } from 'react'

import type { ActivityRecord, TimeDisplayMode } from '@fit-editor/fit-core'

import { useFieldCell } from '@/components/data/field-cell-renderer'
import { ColumnHeading } from '@/components/data/message-table'
import {
  computeWindow,
  FALLBACK_VIEWPORT_HEIGHT,
  RECORD_ROW_HEIGHT,
} from '@/components/data/records-window'
import type { FitDataModel } from '@/components/data/use-fit-data-model'
import { useUnits } from '@/hooks/use-units'
import { discoverColumns, readColumn } from '@/lib/fit-data'
import { formatFriendlyValue } from '@/lib/fit-data-format'
import { cn } from '@/lib/utils'
import { useActivityStore } from '@/store/activity-store'

const PREFERRED_RECORD_COLUMNS = [
  'timestamp',
  'positionLat',
  'positionLong',
  'distance',
  'enhancedSpeed',
  'speed',
  'heartRate',
  'cadence',
  'power',
  'enhancedAltitude',
  'altitude',
  'temperature',
] as const

export interface RecordsTableProps {
  model: FitDataModel
  source: 'current' | 'original'
  timeMode: TimeDisplayMode
}

/**
 * Every `record` message, windowed: only the rows intersecting the scroll
 * viewport (plus a small overscan) are in the DOM, with spacer rows above
 * and below standing in for the rest, so tens of thousands of records
 * scroll smoothly. Fixed row height keeps the windowing math O(1) per
 * scroll event -- no per-row measurement.
 *
 * Clicking a row (or moving the keyboard cursor with the arrow keys while
 * the table has focus) sets the shared hovered timestamp, which the
 * timeline (and the map, in the Dashboard view) highlight. The row
 * matching the current hovered timestamp -- including one hovered from
 * the timeline -- is highlighted here too.
 */
export function RecordsTable({ model, source, timeMode }: RecordsTableProps) {
  const records = model.activity.records
  const messageMeta = model.profile?.byMessagesKey.get('recordMesgs')
  const columns = useMemo(
    () =>
      discoverColumns(records, {
        messageMeta,
        devFields: model.devFields,
        preferredOrder: PREFERRED_RECORD_COLUMNS,
      }),
    [records, messageMeta, model.devFields],
  )
  const renderCell = useFieldCell()
  const unitSystem = useUnits()
  const helpId = useId()

  const hoveredTimestampMs = useActivityStore((s) => s.hoveredTimestampMs)
  const setHoveredTimestampMs = useActivityStore((s) => s.setHoveredTimestampMs)

  const scrollRef = useRef<HTMLDivElement>(null)
  const [scrollTop, setScrollTop] = useState(0)
  const [viewportHeight, setViewportHeight] = useState(FALLBACK_VIEWPORT_HEIGHT)
  const [cursor, setCursor] = useState<number | null>(null)
  const frameRef = useRef<number | null>(null)

  useEffect(() => {
    const el = scrollRef.current
    if (el == null) return
    const measure = () => {
      if (el.clientHeight > 0) setViewportHeight(el.clientHeight)
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  useEffect(
    () => () => {
      if (frameRef.current != null) cancelAnimationFrame(frameRef.current)
    },
    [],
  )

  const handleScroll = useCallback((event: UIEvent<HTMLDivElement>) => {
    const top = event.currentTarget.scrollTop
    if (typeof requestAnimationFrame === 'undefined') {
      setScrollTop(top)
      return
    }
    if (frameRef.current != null) cancelAnimationFrame(frameRef.current)
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null
      setScrollTop(top)
    })
  }, [])

  // Header row height is the same fixed row height, so the visible body
  // area is the viewport minus one row.
  const { start, end, padTop, padBottom } = computeWindow(
    records.length,
    scrollTop,
    Math.max(0, viewportHeight - RECORD_ROW_HEIGHT),
  )

  const selectRow = useCallback(
    (index: number) => {
      setCursor(index)
      const ts = records[index]?.timestamp
      setHoveredTimestampMs(ts instanceof Date ? ts.getTime() : null)
    },
    [records, setHoveredTimestampMs],
  )

  const scrollRowIntoView = (index: number) => {
    const el = scrollRef.current
    if (el == null) return
    const bodyHeight = Math.max(RECORD_ROW_HEIGHT, el.clientHeight - RECORD_ROW_HEIGHT)
    const rowTop = index * RECORD_ROW_HEIGHT
    let next = el.scrollTop
    if (rowTop < el.scrollTop) next = rowTop
    else if (rowTop + RECORD_ROW_HEIGHT > el.scrollTop + bodyHeight) {
      next = rowTop + RECORD_ROW_HEIGHT - bodyHeight
    }
    if (next !== el.scrollTop) {
      el.scrollTop = next
      setScrollTop(next)
    }
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (records.length === 0) return
    const page = Math.max(1, Math.floor(viewportHeight / RECORD_ROW_HEIGHT) - 1)
    const current = cursor ?? start
    let next: number
    switch (event.key) {
      case 'ArrowDown':
        next = cursor == null ? current : current + 1
        break
      case 'ArrowUp':
        next = current - 1
        break
      case 'PageDown':
        next = current + page
        break
      case 'PageUp':
        next = current - page
        break
      case 'Home':
        next = 0
        break
      case 'End':
        next = records.length - 1
        break
      default:
        return
    }
    event.preventDefault()
    const clamped = Math.max(0, Math.min(records.length - 1, next))
    selectRow(clamped)
    scrollRowIntoView(clamped)
  }

  if (records.length === 0) {
    return <p className="text-muted-foreground text-sm">This file has no record messages.</p>
  }

  const rows: ActivityRecord[] = records.slice(start, end)
  const colSpan = columns.length + 1

  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-muted-foreground text-xs" id={helpId}>
        {records.length.toLocaleString()} records. Click a row, or focus the table and use the arrow
        keys, to highlight that moment on the timeline.
      </p>
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="region"
        aria-label="Records table"
        aria-describedby={helpId}
        data-testid="records-scroll"
        className="border-border focus-visible:ring-ring relative h-[60vh] min-h-64 overflow-auto rounded-md border outline-none focus-visible:ring-2"
      >
        <table
          className="w-full border-collapse text-left text-xs"
          aria-rowcount={records.length + 1}
        >
          <caption className="sr-only">Record messages</caption>
          <thead>
            <tr aria-rowindex={1} className="h-7">
              <th
                scope="col"
                className="bg-muted sticky top-0 z-10 px-2 font-semibold whitespace-nowrap"
              >
                #
              </th>
              {columns.map((column) => (
                <th
                  key={column.id}
                  scope="col"
                  className="bg-muted sticky top-0 z-10 px-2 font-semibold whitespace-nowrap"
                >
                  <ColumnHeading column={column} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {padTop > 0 && (
              <tr aria-hidden="true" style={{ height: padTop }}>
                <td colSpan={colSpan} className="p-0" />
              </tr>
            )}
            {rows.map((record, offset) => {
              const index = start + offset
              const ts = record.timestamp
              const isHovered =
                hoveredTimestampMs != null &&
                ts instanceof Date &&
                ts.getTime() === hoveredTimestampMs
              return (
                <tr
                  key={index}
                  aria-rowindex={index + 2}
                  aria-current={cursor === index ? 'true' : undefined}
                  data-testid="record-row"
                  onClick={() => selectRow(index)}
                  className={cn(
                    'border-border hover:bg-muted/60 h-7 cursor-pointer border-t',
                    isHovered && 'bg-primary/15 hover:bg-primary/20',
                    cursor === index && 'outline-primary outline-1 -outline-offset-1',
                  )}
                >
                  <th
                    scope="row"
                    className="text-muted-foreground px-2 font-normal whitespace-nowrap tabular-nums"
                  >
                    {index + 1}
                  </th>
                  {columns.map((column) => {
                    const value = readColumn(record, column.id)
                    return (
                      <td key={column.id} className="px-2 whitespace-nowrap tabular-nums">
                        {renderCell(
                          {
                            source,
                            surface: 'records',
                            messagesKey: 'recordMesgs',
                            mesgNum: messageMeta?.num,
                            messageIndex: index,
                            fieldId: column.id,
                            kind: column.kind,
                            value,
                            units: column.units,
                            meta: column.meta,
                          },
                          formatFriendlyValue(column.id, value, column.units, timeMode, {
                            precise: true,
                            unitSystem,
                          }),
                        )}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
            {padBottom > 0 && (
              <tr aria-hidden="true" style={{ height: padBottom }}>
                <td colSpan={colSpan} className="p-0" />
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="sr-only" aria-live="polite">
        {cursor != null ? `Record ${cursor + 1} of ${records.length}` : ''}
      </p>
    </div>
  )
}
